import React, { useState } from 'react';

// Define learning progress report data structure
interface LearningReport {
  totalCharacters: number;
  learnedCharacters: number;
  accuracyRate: number;
  totalTests: number;
  averageScore: number;
  learningDays: number;
  dailyProgress: {
    date: string;
    charactersLearned: number;
  }[];
  recentTests: {
    id: string;
    date: string;
    score: number;
    level: string;
  }[];
}

const ReportPage: React.FC = () => {
  // Mock learning progress report data
  const [reportData, setReportData] = useState<LearningReport>({
    totalCharacters: 50,
    learnedCharacters: 23,
    accuracyRate: 87,
    totalTests: 4,
    averageScore: 85,
    learningDays: 12,
    dailyProgress: [
      { date: '2026-01-01', charactersLearned: 2 },
      { date: '2026-01-02', charactersLearned: 3 },
      { date: '2026-01-03', charactersLearned: 1 },
      { date: '2026-01-04', charactersLearned: 4 },
      { date: '2026-01-05', charactersLearned: 2 },
      { date: '2026-01-06', charactersLearned: 3 },
      { date: '2026-01-07', charactersLearned: 2 },
      { date: '2026-01-08', charactersLearned: 5 },
      { date: '2026-01-09', charactersLearned: 1 },
      { date: '2026-01-10', charactersLearned: 4 },
      { date: '2026-01-11', charactersLearned: 3 },
      { date: '2026-01-12', charactersLearned: 5 }
    ],
    recentTests: [
      { id: 'test1', date: '2026-01-10', score: 90, level: 'HSK 1' },
      { id: 'test2', date: '2026-01-08', score: 85, level: 'HSK 1' },
      { id: 'test3', date: '2026-01-06', score: 75, level: 'HSK 1' },
      { id: 'test4', date: '2026-01-04', score: 80, level: 'HSK 1' }
    ]
  });

  // Calculate learning completion rate
  const completionRate = Math.round((reportData.learnedCharacters / reportData.totalCharacters) * 100);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Learning Progress Report</h1>
          <p className="text-lg text-gray-600 mt-2">View your Chinese learning progress and achievements</p>
        </div>

        {/* Overall Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Learned Characters</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{reportData.learnedCharacters}/{reportData.totalCharacters}</h3>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full" style={{ width: `${completionRate}%` }}></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">Completion: {completionRate}%</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Accuracy</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{reportData.accuracyRate}%</h3>
              </div>
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Test Score</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{reportData.averageScore}</h3>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Learning History and Test Records */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Learning Progress */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Daily Learning Progress</h2>
              <span className="text-sm text-gray-600">{reportData.learningDays} days</span>
            </div>
            
            <div className="space-y-4">
              {reportData.dailyProgress.map((day, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{new Date(day.date).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{day.charactersLearned}</span>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${Math.min((day.charactersLearned / 10) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Test Records */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Test Records</h2>
              <span className="text-sm text-gray-600">{reportData.totalTests} tests</span>
            </div>
            
            <div className="space-y-4">
              {reportData.recentTests.map((test) => (
                <div key={test.id} className="p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{new Date(test.date).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-600 mt-1">{test.level}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">{test.score}</p>
                      <p className="text-xs text-gray-600 mt-1">points</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;